import { useMemo } from 'react'
import { CanvasTexture, Sprite, SpriteMaterial, Vector3 } from 'three'
import { calculateTextWidth } from './utils'

type EdgeAnchorLocation = 'top' | 'bottom' | 'left' | 'right'
type CornerAnchorLocation = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
export type AnchorLocation = 'center' | EdgeAnchorLocation | CornerAnchorLocation

interface Props {
  textLines: string[]
  color: string
  backgroundColor?: string
  borderColor?: string
  borderWidth?: number
  position: Vector3
  scale?: number
  scaleAxis?: 'X' | 'Y'
  useScaleYForSingleLine?: boolean
  opacity?: number
  anchorLocation?: AnchorLocation
  anchorPlane?: 'xy' | 'xz' | 'yz'
  margin?: number
  /** Counter-clockwise screen rotation in radians; the anchor rotates along (xy plane only). */
  rotation?: number
}

/**
 * A reusable component that creates a text sprite with multiple lines of text.
 * Uses Three.js Sprite and CanvasTexture to render text in 3D space.
 */
const TextSprite = ({
  textLines,
  color,
  backgroundColor,
  borderColor,
  borderWidth,
  position,
  scale = 0.5,
  scaleAxis = 'Y',
  useScaleYForSingleLine = true,
  opacity = 0.75,
  anchorLocation = 'center',
  anchorPlane = 'xy',
  margin = 20,
  rotation = 0,
}: Props) => {
  // Calculate canvas dimensions first

  const fontSize = 100
  const lineGapSymmetricSingleSided = fontSize * 0.1
  const marginY = margin
  const marginX = marginY
  const numLines = textLines.length
  const textWidths = textLines.map(line => calculateTextWidth(line, fontSize))
  const maxTextWidth = Math.max(...textWidths)

  const canvasWidth = maxTextWidth + 2 * marginX
  const canvasHeight =
    numLines * fontSize + numLines * lineGapSymmetricSingleSided * 2 + 2 * marginY
  const aspectRatio = canvasHeight / canvasWidth

  const adjustedScale =
    useScaleYForSingleLine && scaleAxis === 'Y' ? scale * numLines * (1 + 0.2) : scale
  // Calculate sprite dimensions in 3D space based on scale axis
  const spriteWidth = scaleAxis === 'Y' ? adjustedScale / aspectRatio : adjustedScale
  const spriteHeight = scaleAxis === 'Y' ? adjustedScale : adjustedScale * aspectRatio

  // Create the sprite with the given text and styling
  const sprite = useMemo(() => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    canvas.width = canvasWidth
    canvas.height = canvasHeight

    if (context) {
      // Set background if provided
      if (backgroundColor) {
        context.fillStyle = backgroundColor
        context.fillRect(0, 0, canvas.width, canvas.height)
      }

      // Draw border if provided
      if (borderColor && borderWidth && borderWidth > 0) {
        context.strokeStyle = borderColor
        context.lineWidth = borderWidth
        context.strokeRect(
          borderWidth / 2,
          borderWidth / 2,
          canvas.width - borderWidth,
          canvas.height - borderWidth,
        )
      }

      const deltaTextY = fontSize + lineGapSymmetricSingleSided

      // Set text properties
      context.fillStyle = color
      context.textAlign = 'center'
      // need the top baseline so that the coordinate calculation is correct
      context.textBaseline = 'top'
      context.font = `${fontSize}px Arial`

      const firstLineY = marginY + lineGapSymmetricSingleSided * 2

      // Draw each line of text
      textLines.forEach((line, index) => {
        const lineY = firstLineY + index * deltaTextY
        context.fillText(line, canvas.width / 2, lineY)
      })
    }

    const texture = new CanvasTexture(canvas)
    const spriteMaterial = new SpriteMaterial({
      map: texture,
      transparent: true,
      opacity,
      alphaTest: 0.01,
      rotation,
    })
    const newSprite = new Sprite(spriteMaterial)

    // Set scale based on calculated values
    newSprite.scale.set(spriteWidth, spriteHeight, 1)

    return newSprite
  }, [
    textLines,
    color,
    backgroundColor,
    borderColor,
    borderWidth,
    canvasWidth,
    canvasHeight,
    spriteWidth,
    spriteHeight,
    lineGapSymmetricSingleSided,
    marginY,
    fontSize,
    opacity,
    rotation,
  ])

  // Calculate position offset based on anchor location
  const adjustedPosition = useMemo(() => {
    let offsetX = 0
    let offsetY = 0
    let offsetZ = 0

    const edgeAnchors =
      anchorLocation === 'center' ? [] : (anchorLocation.split('-') as EdgeAnchorLocation[])

    edgeAnchors.forEach(edgeAnchor => {
      switch (edgeAnchor) {
        case 'top':
          switch (anchorPlane) {
            case 'xy':
              offsetY = -spriteHeight / 2
              break
            case 'xz':
              offsetZ = -spriteHeight / 2
              break
            case 'yz':
              offsetY = -spriteWidth / 2
              break
            default:
              break
          }
          break
        case 'bottom':
          switch (anchorPlane) {
            case 'xy':
              offsetY = spriteHeight / 2
              break
            case 'xz':
              offsetZ = spriteHeight / 2
              break
            case 'yz':
              offsetY = spriteWidth / 2
              break
            default:
              break
          }
          break
        case 'left':
          switch (anchorPlane) {
            case 'xy':
              offsetX = spriteWidth / 2
              break
            case 'xz':
              offsetZ = spriteWidth / 2
              break
            case 'yz':
              offsetY = spriteHeight / 2
              break
            default:
              break
          }
          break
        case 'right':
          switch (anchorPlane) {
            case 'xy':
              offsetX = -spriteWidth / 2
              break
            case 'xz':
              offsetZ = -spriteWidth / 2
              break
            case 'yz':
              offsetY = -spriteHeight / 2
              break
            default:
              break
          }
          break
        default:
          break
      }
    })
    if (rotation !== 0 && anchorPlane === 'xy') {
      const cos = Math.cos(rotation)
      const sin = Math.sin(rotation)
      const rotatedX = offsetX * cos - offsetY * sin
      const rotatedY = offsetX * sin + offsetY * cos
      offsetX = rotatedX
      offsetY = rotatedY
    }
    const offsetVector = new Vector3(offsetX, offsetY, offsetZ)
    return position.clone().add(offsetVector)
  }, [position, spriteWidth, spriteHeight, anchorLocation, anchorPlane, rotation])

  return <primitive object={sprite} position={adjustedPosition} />
}

export default TextSprite
